import { Banknote, CheckCircle2, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/common/Button';
import { DataTable } from '../../components/common/DataTable';
import { EmptyState } from '../../components/common/EmptyState';
import { FormField } from '../../components/common/FormField';
import { LoadingState } from '../../components/common/LoadingState';
import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { createTransaction, getEmployee, getEmployees, getTransactions } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useTenant } from '../../hooks/useTenant';
import { useToast } from '../../contexts/ToastContext';
import { formatCurrency } from '../../utils/currency';
import { formatDate, formatDateTime } from '../../utils/date';
import { getPayrollSummary } from '../../utils/payroll';

const CURRENT_PERIOD = new Date().toISOString().slice(0, 7);
const EMPTY_VALUES = { employeeId: '', amount: '', paymentDate: new Date().toISOString().slice(0, 10), salaryPeriod: CURRENT_PERIOD, paymentMethod: 'bank_transfer', reference: '', note: '' };

function formatMethod(value) {
  const text = String(value || '').replace(/[_-]+/g, ' ').trim();
  return text ? `${text[0].toUpperCase()}${text.slice(1)}` : 'Bank transfer';
}

function generateSalaryReference() {
  return `SAL${new Date().getFullYear().toString().slice(-2)}${String(Date.now()).slice(-6)}`;
}

export function PaySalariesPage() {
  const { user } = useAuth();
  const { activeTenant, activeTenantId } = useTenant();
  const { notify } = useToast();
  const currency = activeTenant?.settings?.currency || 'KES';
  const [employees, setEmployees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [modalMode, setModalMode] = useState('individual');
  const [currentPeriod, setCurrentPeriod] = useState(CURRENT_PERIOD);
  const [values, setValues] = useState(EMPTY_VALUES);

  const employeeOptions = useMemo(() => {
    const currentEmail = String(user?.email || '').trim().toLowerCase();
    const hasCurrentUser = employees.some((employee) => String(employee.email || '').trim().toLowerCase() === currentEmail && currentEmail);
    if (hasCurrentUser || !user) return employees;
    return [{ id: 'current-user', fullName: user.displayName || user.email || 'Current user', jobTitle: 'Account owner', email: user.email || '', phone: '' }, ...employees];
  }, [employees, user]);

  const load = useCallback(async () => {
    if (!activeTenantId) {
      setEmployees([]);
      setPayments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [loadedEmployees, nextTransactions] = await Promise.all([getEmployees(activeTenantId), getTransactions(activeTenantId)]);
      let nextEmployees = loadedEmployees;
      const currentEmail = String(user?.email || '').trim().toLowerCase();
      const hasCurrentUser = nextEmployees.some((employee) => String(employee.email || '').trim().toLowerCase() === currentEmail && currentEmail);
      if (user && !hasCurrentUser) {
        const savedCurrentUser = await getEmployee(activeTenantId, 'current-user');
        if (savedCurrentUser) nextEmployees = [savedCurrentUser, ...nextEmployees];
      }
      setEmployees(nextEmployees);
      setPayments(nextTransactions.filter((item) => item.category === 'salary'));
    } catch (error) {
      console.error('Unable to load salary payments', error);
      notify('Unable to load salary payments. Try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTenantId, notify, user?.email]);

  useEffect(() => {
    load().catch((error) => console.error(error));
  }, [load]);

  const selectedEmployee = useMemo(
    () => employeeOptions.find((employee) => employee.id === values.employeeId) || null,
    [employeeOptions, values.employeeId],
  );

  const totalPaid = useMemo(() => payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0), [payments]);

  const payrollRows = useMemo(() => employeeOptions.map((employee) => ({
    ...employee,
    payroll: getPayrollSummary(employee, currentPeriod, payments),
  })), [currentPeriod, employeeOptions, payments]);

  const totalOutstanding = useMemo(() => payrollRows.reduce((sum, row) => sum + row.payroll.remaining, 0), [payrollRows]);

  function openModal(mode = 'individual', employeeId = '') {
    const firstEmployee = employeeOptions[0];
    const selected = payrollRows.find((row) => row.id === employeeId) || payrollRows[0] || firstEmployee;
    const selectedPayroll = selected?.payroll || getPayrollSummary(selected, currentPeriod, payments);
    setModalMode(mode);
    setValues({ ...EMPTY_VALUES, employeeId: selected?.id || '', salaryPeriod: currentPeriod, amount: mode === 'individual' && selectedPayroll?.remaining ? String(selectedPayroll.remaining) : '' });
    setOpen(true);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (saving || !values.paymentDate || !values.salaryPeriod) return;

    setSaving(true);
    try {
      const rowsToPay = modalMode === 'all'
        ? payrollRows.filter((row) => row.status !== 'inactive' && row.payroll.remaining > 0)
        : [payrollRows.find((row) => row.id === values.employeeId)].filter(Boolean);
      if (!rowsToPay.length) {
        notify('There are no outstanding salaries for this period.', 'error');
        return;
      }
      const enteredAmount = Number(values.amount);
      if (modalMode === 'individual' && (!Number.isFinite(enteredAmount) || enteredAmount <= 0)) {
        notify('Enter a payment amount greater than zero.', 'error');
        return;
      }

      await Promise.all(rowsToPay.map((row) => {
        const amount = modalMode === 'all' ? row.payroll.remaining : enteredAmount;
        return createTransaction(activeTenantId, user?.uid, {
          type: 'expense', category: 'salary', source: 'manual', employeeId: row.id, employeeName: row.fullName,
          employeeEmail: row.email, employeePhone: row.phone, amount, paymentDate: values.paymentDate,
          salaryPeriod: values.salaryPeriod, salaryRate: row.payroll.salary, paymentMethod: values.paymentMethod,
          reference: values.reference || generateSalaryReference(), note: values.note, status: 'paid',
        });
      }));
      notify(modalMode === 'all' ? `Paid ${rowsToPay.length} employee salary${rowsToPay.length === 1 ? '' : 'ies'} in full.` : 'Salary payment recorded successfully.');
      setOpen(false);
      // After recording a historical payment, show the current period so the
      // newly accumulated dues from the payroll start month are visible.
      setCurrentPeriod(CURRENT_PERIOD);
      await load();
    } catch (error) {
      console.error('Unable to record salary payment', error);
      notify('Unable to record salary payment. Try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="employees-page payroll-page">
      <PageHeader title="Pay Salaries" description="Pay salaries in full, make partial payments, and carry balances into future periods." action={<div className="payroll-header-actions"><Button variant="secondary" icon={CheckCircle2} onClick={() => openModal('all')}>Pay everyone in full</Button><Button icon={Plus} onClick={() => openModal('individual')}>Pay selected employee</Button></div>} />
      {loading ? <LoadingState label="Loading salary payments..." /> : (
        <>
          <section className="payroll-toolbar"><label>Salary period<input type="month" value={currentPeriod} onChange={(event) => { setCurrentPeriod(event.target.value); setValues((current) => ({ ...current, salaryPeriod: event.target.value })); }} /></label><span>Balances carry forward automatically from earlier periods.</span></section>
          <section className="payroll-summary"><div><span>Total recorded</span><strong>{formatCurrency(totalPaid, currency)}</strong></div><div><span>Outstanding this period</span><strong>{formatCurrency(totalOutstanding, currency)}</strong></div><div><span>Employees</span><strong>{employeeOptions.length}</strong></div></section>
          <DataTable
            rows={payments}
            columns={[
              { key: 'paymentDate', label: 'Payment date', render: (item) => <div className="payroll-date"><strong>{formatDate(item.paymentDate)}</strong><span>{formatDateTime(item.createdAt)}</span></div> },
              { key: 'employeeName', label: 'Employee' },
              { key: 'amount', label: 'Amount', render: (item) => formatCurrency(item.amount, currency) },
              { key: 'salaryPeriod', label: 'Salary period', render: (item) => item.salaryPeriod || '—' },
              { key: 'paymentMethod', label: 'Method', render: (item) => formatMethod(item.paymentMethod) },
              { key: 'reference', label: 'Reference' },
            ]}
            empty={<EmptyState title="No salary payments yet." message="Use Pay Salary to record the first payment for an employee." icon={Banknote} />}
          />
        </>
      )}

      <Modal className="employee-modal" open={open} onClose={() => setOpen(false)} title={modalMode === 'all' ? 'Pay everyone in full' : 'Pay selected employee'} description={modalMode === 'all' ? `Record full outstanding balances for every employee for ${currentPeriod}.` : 'Record a full or partial payment for an employee.'}>
        <form className="modal-form" onSubmit={handleSubmit}>
          {modalMode === 'individual' ? <FormField label="Employee" id="salary-employee"><select id="salary-employee" value={values.employeeId} onChange={(event) => { const next = payrollRows.find((item) => item.id === event.target.value); setValues({ ...values, employeeId: event.target.value, amount: next?.payroll.remaining ? String(next.payroll.remaining) : '' }); }} required><option value="">Select an employee</option>{payrollRows.map((employee) => <option key={employee.id} value={employee.id}>{employee.fullName} · remaining {formatCurrency(employee.payroll.remaining, currency)}</option>)}</select></FormField> : <div className="payroll-modal-notice">Every active employee with an outstanding balance for {currentPeriod} will be paid in full. Previous balances are included first.</div>}
          {modalMode === 'individual' ? <div className="payroll-modal-balance">{selectedEmployee ? <><span>Outstanding for {selectedEmployee.fullName}</span><strong>{formatCurrency(payrollRows.find((row) => row.id === selectedEmployee.id)?.payroll.remaining || 0, currency)}</strong></> : null}</div> : null}
          {modalMode === 'individual' ? <FormField label={`Amount (${currency})`} id="salary-amount"><input id="salary-amount" type="number" min="0.01" step="0.01" max={payrollRows.find((row) => row.id === values.employeeId)?.payroll.remaining || undefined} value={values.amount} onChange={(event) => setValues({ ...values, amount: event.target.value })} required /></FormField> : null}
          <div className="form-grid"><FormField label="Payment date" id="salary-date"><input id="salary-date" type="date" value={values.paymentDate} onChange={(event) => setValues({ ...values, paymentDate: event.target.value })} required /></FormField><FormField label="Salary period" id="salary-period"><input id="salary-period" type="month" value={values.salaryPeriod} onChange={(event) => setValues({ ...values, salaryPeriod: event.target.value })} required /></FormField></div>
          <FormField label="Payment method" id="salary-method"><select id="salary-method" value={values.paymentMethod} onChange={(event) => setValues({ ...values, paymentMethod: event.target.value })}><option value="bank_transfer">Bank transfer</option><option value="mpesa">M-Pesa</option><option value="cash">Cash</option><option value="cheque">Cheque</option></select></FormField>
          <FormField label="Reference" id="salary-reference"><input id="salary-reference" placeholder="Optional payment reference" value={values.reference} onChange={(event) => setValues({ ...values, reference: event.target.value })} /></FormField>
          <FormField label="Note" id="salary-note"><textarea id="salary-note" rows="3" placeholder="Optional note for this payment" value={values.note} onChange={(event) => setValues({ ...values, note: event.target.value })} /></FormField>
          <div className="form-actions"><Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" loading={saving}>Record payment</Button></div>
        </form>
      </Modal>
    </div>
  );
}
